import Styled, { css } from 'styled-components';

import { media } from '../../../../lib/StyledComponents';
import { SVGIconStyleWrapper } from '../../../Icon/SVGIcon';
import { cssDeclaration } from '../../Utils';
import { IFocusControl, IHoveredProps, IInputError } from '../common';
import { FormError } from '../FormStyles';

interface ICheckBoxInput extends IInputError {
  iconType?: 'mixed' | 'done';
}

export const CheckboxContain = Styled.div<IFocusControl>`
  display: flex;
  flex-wrap: wrap;
  position: relative;
  margin-bottom: 1em;
  width: fit-content;
  padding: 7px 0;
  border-color: transparent;

  ${({ theme: { themeTwo }, clickFocus }) => {
    const { form: { checkbox }} = themeTwo;

    return css`
      /* padding */ ${checkbox.padding && cssDeclaration(themeTwo, 'padding', checkbox.padding)};

      ${checkbox.state.focus.focusPosition && css`
        padding-bottom:
          calc(2px + (2 * ${themeTwo.global.edgeSize[themeTwo.global.outline.size] || themeTwo.global.outline.size }));
      `}

      ${checkbox.state.focus.outline && !clickFocus && css`
        &.focus {
          /* outline */ ${cssDeclaration(themeTwo, 'outline', checkbox.state.focus.outline)};
        }
      `}
    `;
  }}

  ${media.medium`
    margin-bottom: 0;
  `}

  ${FormError} {
    flex-grow: 0;
    flex-shrink: 1;
    flex-basis: 100%;
  }
`;

export const CheckboxLabel = Styled.div`
  vertical-align: super;
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: 0;

  &:hover {
    cursor: pointer;
  }

  ${({ theme: { themeTwo }}) => {
    const { form: { checkbox: { label }}} = themeTwo;

    return css`
      line-height: ${label.font.lineHeight};

      /* color */ ${cssDeclaration(themeTwo, 'color', label.font.color)};
      /* font-size */ ${label.font.size && cssDeclaration(themeTwo, 'font-size', label.font.size)};
      /* transform */ ${label.verticalOffset && cssDeclaration(themeTwo, 'transform', label.verticalOffset)};
    `;
  }}
`;

export const CheckboxInput = Styled.input<ICheckBoxInput & IFocusControl & IHoveredProps>`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none !important;
  margin-right: 0.5em;
  cursor: pointer;
  outline: none;
  position: relative;
  flex-grow: 0;
  flex-shrink: 1;
  background-color: transparent;

  ${({ theme, error, clickFocus, isHovered: labelIsHovered }) => {
    // tslint:disable-next-line
    const themeTwo = theme.themeTwo;
    const { form: { checkbox }} = themeTwo;
    const {
      state: {
        checked,
        checkedHover,
        disabled,
        disabledChecked,
         error: errorState,
        focus,
        hover,
      },
    } = checkbox;
    const inputSize = themeTwo.global.edgeSize[checkbox.size];
    const iconSize = themeTwo.global.edgeSize[checkbox.icon.size] || checkbox.icon.size;
    const iconHorizontalOffset =
      checkbox.margin.left ? (themeTwo.global.edgeSize[checkbox.margin.left] || checkbox.margin.left) : undefined;

    return css`
      z-index: ${themeTwo.global.zIndex.middle};
      flex-basis: ${inputSize};
      width: ${inputSize};
      height: ${inputSize};
      /* border-radius */ ${cssDeclaration(themeTwo, 'border-radius', checkbox.round)};
      /* border */ ${cssDeclaration(themeTwo, 'border', checkbox.border)};
      /* box-shadow */ ${checkbox.shadow && cssDeclaration(themeTwo, 'box-shadow', checkbox.shadow)};
      /* margin */ ${checkbox.margin && cssDeclaration(themeTwo, 'margin', checkbox.margin)};

      & ~ ${SVGIconStyleWrapper} {
        display: none;
        /* background-color */ ${cssDeclaration(themeTwo, 'background-color', checkbox.backgroundColor)};
      }

      &:checked {
        /* border */ ${checked.border && cssDeclaration(themeTwo, 'border', checked.border)};
        /* box-shadow */ ${checked.shadow && cssDeclaration(themeTwo, 'box-shadow', checked.shadow)};

        & + ${CheckboxLabel} {
          /* color */ ${checked.label && cssDeclaration(themeTwo, 'color', checked.label.font.color)}
        }

        & ~ ${SVGIconStyleWrapper} {
          display: flex;
          justify-content: center;
          align-items: center;
          border: 0;
          position: absolute;
          animation: draw-checkbox ease-in-out 0.2s forwards;
          z-index: ${themeTwo.global.zIndex.low};
          height: ${inputSize};
          width: ${inputSize};

          /* background-color */ ${checked.backgroundColor && cssDeclaration(themeTwo, 'background-color', checked.backgroundColor)};
          /* border-radius */ ${cssDeclaration(themeTwo, 'border-radius', checkbox.round)};

          ${iconHorizontalOffset && css`
            left: ${iconHorizontalOffset};
          `}

          & svg {
            height: ${iconSize};
            width: ${iconSize};

            path {
              /* fill */ ${cssDeclaration(themeTwo, 'fill', checkbox.icon.color)};
            }

            // need to figure out a solution to handle check svg that use stroke not fill
            #colorStroke {
              fill: none;
              /* stroke */ ${cssDeclaration(themeTwo, 'stroke', checkbox.icon.color)};
            }
          }
        }

        &:hover {
          /* border */ ${checkedHover && checkedHover.border && cssDeclaration(themeTwo, 'border', checkedHover.border)};

          & ~ ${SVGIconStyleWrapper} {
            /* background-color */
            ${checkedHover && checkedHover.backgroundColor &&
              cssDeclaration(themeTwo, 'background-color', checkedHover.backgroundColor)
            };
          }
        }

        ${labelIsHovered && css`
          /* border */ ${checkedHover && checkedHover.border && cssDeclaration(themeTwo, 'border', checkedHover.border)};

          & ~ ${SVGIconStyleWrapper} {
            /* background-color */
            ${checkedHover && checkedHover.backgroundColor &&
              cssDeclaration(themeTwo, 'background-color', checkedHover.backgroundColor)
            };
          }
        `}
      }

      &:disabled {
        cursor: not-allowed;
        /* border */ ${disabled.border && cssDeclaration(themeTwo, 'border', disabled.border)};
        /* box-shadow */ ${disabled.shadow && cssDeclaration(themeTwo, 'box-shadow', disabled.shadow)};

        ${disabled.backgroundColor && css`
          /* background-color */ ${cssDeclaration(themeTwo, 'background-color', disabled.backgroundColor)};

          & ~ ${SVGIconStyleWrapper} {
            /* background-color */ ${cssDeclaration(themeTwo, 'background-color', disabled.backgroundColor)};
          }
        `}

        & + ${CheckboxLabel} {
          cursor: not-allowed;
          /* color */ ${disabled.label && disabled.label.font && cssDeclaration(themeTwo, 'color', disabled.label.font.color)};
        }

        &:hover {
          cursor: not-allowed;
          /* border */ ${disabled.border && cssDeclaration(themeTwo, 'border', disabled.border)};
          /* box-shadow */ ${disabled.shadow && cssDeclaration(themeTwo, 'box-shadow', disabled.shadow)};

          & + ${CheckboxLabel} {
            cursor:  not-allowed;
            /* color */ ${disabled.label && cssDeclaration(themeTwo, 'color', disabled.label.font.color)};
          }
        }

        &:checked {
          /* box-shadow */ ${disabledChecked.shadow && cssDeclaration(themeTwo, 'box-shadow', disabledChecked.shadow)};

          ${disabledChecked.backgroundColor && css`
            background-color: transparent;
          `}

          & ~ ${SVGIconStyleWrapper} {
            /* background-color */
            ${disabledChecked.backgroundColor &&
              cssDeclaration(themeTwo, 'background-color', disabledChecked.backgroundColor)
            };

            ${disabledChecked.icon && css`
              svg {
                path {
                  /* fill */ ${cssDeclaration(themeTwo, 'fill', disabledChecked.icon.color)};
                }

                // need to figure out a solution to handle check svg that use stroke not fill
                #colorStroke {
                  fill: none;
                  /* stroke */ ${cssDeclaration(themeTwo, 'stroke', disabledChecked.icon.color)};
                }
              }
            `}
          }
        }

        ${labelIsHovered && css`
          cursor: not-allowed;
            /* border */ ${disabled.border && cssDeclaration(themeTwo, 'border', disabled.border)};
            /* box-shadow */ ${disabled.shadow && cssDeclaration(themeTwo, 'box-shadow', disabled.shadow)};

            & + ${CheckboxLabel} {
              cursor: not-allowed;
              /* color */ ${disabled.label && cssDeclaration(themeTwo, 'color', disabled.label.font.color)};
          }
        `}
      }

      ${!clickFocus && css`
        &:focus {
          /* box-shadow */ ${focus.shadow && cssDeclaration(themeTwo, 'box-shadow', focus.shadow)};

          ${focus.focusPosition === 'indicatorOnly' && focus.outline && css`
            /* outline */ ${cssDeclaration(themeTwo, 'outline', focus.outline)};
          `}

          :hover {
            /* border */ ${cssDeclaration(themeTwo, 'border', checkbox.border)};
          }
        }
      `}

      &:hover {
        cursor: pointer;
        /* border */ ${hover.border && cssDeclaration(themeTwo, 'border', hover.border)};
        /* box-shadow */ ${hover.shadow && cssDeclaration(themeTwo, 'box-shadow', hover.shadow)};

        & ~ ${SVGIconStyleWrapper} {
          /* background-color */ ${hover.backgroundColor && cssDeclaration(themeTwo, 'background-color', hover.backgroundColor)};
        }

        & + ${CheckboxLabel} {
          /* color */ ${hover.label && cssDeclaration(themeTwo, 'color', hover.label.font.color)};
        }
      }

      ${error && css`
        /* border */ ${errorState.border && cssDeclaration(themeTwo, 'border', errorState.border)};
        /* box-shadow */ ${errorState.shadow && cssDeclaration(themeTwo, 'box-shadow', errorState.shadow)};

        & + ${CheckboxLabel} {
          /* color */ ${errorState.label && cssDeclaration(themeTwo, 'color', errorState.label.font.color)};
        }

        &:hover {
          /* border */ ${errorState.border && cssDeclaration(themeTwo, 'border', errorState.border)};

          & + ${CheckboxLabel} {
            /* color */ ${errorState.label && cssDeclaration(themeTwo, 'color', errorState.label.font.color)};
          }
        }
      `}

      ${labelIsHovered && css`
        /* border */ ${hover.border && cssDeclaration(themeTwo, 'border', hover.border)};
        /* box-shadow */ ${hover.shadow && cssDeclaration(themeTwo, 'box-shadow', hover.shadow)};

        & ~ ${SVGIconStyleWrapper} {
          /* background-color */ ${hover.backgroundColor && cssDeclaration(themeTwo, 'background-color', hover.backgroundColor)};
        }

        & + ${CheckboxLabel} {
          /* color */ ${hover.label && cssDeclaration(themeTwo, 'color', hover.label.font.color)};
        }
      `}
    `;
  }}
`;
